/**
 * PENGUIN WELLNESS — Visitor Behavior Tracker
 * ============================================================================
 * Store:  Penguin Wellness ONLY (www.penguin-wellness.com / c0iij1-th)
 * Sends:  click heatmap + scroll depth + rrweb session replay
 * To:     hub.penguin-method.com/api/track/* → wellnessdb (Wellness-only DB)
 * Viewer: hub.penguin-method.com/heatmap (Penguin Wellness workspace)
 *
 * Ported from the Penguin Method quiz tracker, adapted for a Shopify MPA:
 *   - one heatmap "session" row per PAGE VIEW, identified by a random
 *     pageViewToken (server upserts on it — retries/beacons are idempotent);
 *     pw_visitor_sid groups a visit's page views together
 *   - one rrweb recording per VISIT (spans pages); meta.visitorSid links the
 *     recording to its heatmap sessions for the dashboard's Replay button
 *   - no DOM-snapshot capture and no mousemove event rows (leaner than Method)
 *   - quizSlug carries the Shopify template name; completed=1 on checkout start
 *   - click data carries BOTH page-based percents (xPercent/yPercent vs the
 *     full document) and viewport percents (vxPercent/vyPercent)
 *   - all form inputs masked in replays (maskAllInputs) — never records PII
 *
 * Privacy/quality guards: skips bots/webdriver, iframes, theme editor AND
 * theme previews (*.myshopify.com / preview_theme_id), DNT/GPC, and respects
 * Shopify's Customer Privacy consent API if active.
 * ============================================================================
 */
(function () {
  'use strict';

  // ── Config ────────────────────────────────────────────────────────────────
  var HUB = 'https://hub.penguin-method.com';
  var HEATMAP_ENDPOINT = HUB + '/api/track/heatmap';
  var REPLAY_ENDPOINT = HUB + '/api/track/replay';
  var FLUSH_MS = 5000;              // heatmap flush cadence
  var REPLAY_FLUSH_MS = 10000;      // rrweb batch cadence
  var RECORDING_SAMPLE_RATE = 1.0;  // record EVERY visit (Mohamed 2026-07-02, no sampling)
  var RRWEB_SRC = 'https://cdn.jsdelivr.net/npm/rrweb@2.0.0-alpha.4/dist/rrweb.min.js';
  // rrweb 2.0.0-alpha.4 matches the hub's rrweb-player 1.0.0-alpha.4 (the
  // combo already proven on the Penguin Method quiz). Do not bump casually.

  var RAGE_THRESHOLD = 3, RAGE_WINDOW = 1000, RAGE_RADIUS = 30;
  var THRASH_CHANGES = 4, THRASH_WINDOW = 500;
  var MAX_EVENT_BUFFER = 300, MAX_REPLAY_BUFFER = 3000;
  var BEACON_CHUNK_BYTES = 55000;   // Safari/Chrome sendBeacon quota ≈ 64KB

  // ── Should we run at all? ────────────────────────────────────────────────
  try {
    if (window.top !== window.self) return;                    // inside iframe
    if (navigator.webdriver) return;                           // automation/bot
    if (window.Shopify && window.Shopify.designMode) return;   // theme editor
    // Theme previews / staff QA: never pollute real visitor analytics
    if (location.hostname.slice(-14) === '.myshopify.com') return;
    if (/[?&]preview_theme_id=/.test(location.search)) return;
    if (navigator.doNotTrack === '1' || window.doNotTrack === '1') return;
    if (navigator.globalPrivacyControl) return;
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.analyticsProcessingAllowed === 'function' &&
        !cp.analyticsProcessingAllowed()) return;
  } catch (e) { /* guards are best-effort */ }

  // ── Page context (injected by snippets/scripts.liquid) ───────────────────
  var scriptEl = document.currentScript ||
    document.querySelector('script[src*="penguin-wellness-tracker"]');
  var PAGE_TYPE = ((scriptEl && scriptEl.getAttribute('data-page-type')) || '')
    .slice(0, 20) || null;

  function randomId(prefix) {
    var rand = '';
    try {
      var bytes = new Uint8Array(12);
      crypto.getRandomValues(bytes);
      for (var i = 0; i < bytes.length; i++) {
        rand += ('0' + bytes[i].toString(16)).slice(-2);
      }
    } catch (e) {
      rand = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    }
    return prefix + Date.now() + '_' + rand;
  }

  function makeSid(key, prefix) {
    try {
      var v = sessionStorage.getItem(key);
      if (!v) {
        v = randomId(prefix);
        sessionStorage.setItem(key, v);
      }
      return v;
    } catch (e) {
      return randomId(prefix);
    }
  }

  var VISITOR_SID = makeSid('pw_visitor_sid', 'v_');
  // High-entropy per-page-view token: the server upserts the session row on
  // it, so lost responses/retries/beacons can never create duplicates, and
  // rows can't be targeted by guessing ids.
  var PAGE_VIEW_TOKEN = randomId('pv').replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
  var device = window.innerWidth < 768 ? 'mobile'
    : window.innerWidth < 1024 ? 'tablet' : 'desktop';
  var dead = false;          // hub said pipeline off (503) — stop everything
  var backoffUntil = 0;      // 429 backoff

  // ════════════════════════════════════════════════════════════════════════
  // Pipeline 1 — heatmap (clicks / scroll / frustration signals)
  // ════════════════════════════════════════════════════════════════════════
  var startTime = Date.now();
  var events = [];
  var maxScroll = 0, totalClicks = 0, rageClicks = 0, completed = false;
  var tick = 0;
  var heatmapInFlight = false;

  var sessionMeta = {
    sessionId: VISITOR_SID,
    pageUrl: location.href.slice(0, 500),
    device: device,
    screenW: window.screen.width,
    screenH: window.screen.height,
    viewportW: window.innerWidth,
    viewportH: window.innerHeight,
    userAgent: navigator.userAgent,
    referrer: document.referrer || null,
    quizSlug: PAGE_TYPE,   // Shopify template name (schema parity w/ Method)
    quizCode: null
  };

  function pushEvent(ev) {
    ev.timestamp = Date.now() - startTime;
    events.push(ev);
    if (events.length > MAX_EVENT_BUFFER) events.splice(0, events.length - MAX_EVENT_BUFFER);
  }

  function cssSelector(el) {
    try {
      if (!el || el.nodeType !== 1) return null;
      if (el.id) return '#' + el.id;
      var parts = [], node = el, depth = 0;
      while (node && node.nodeType === 1 && depth < 5) {
        var part = node.tagName.toLowerCase();
        if (node.classList && node.classList.length) {
          part += '.' + Array.prototype.slice.call(node.classList, 0, 2).join('.');
        }
        parts.unshift(part);
        node = node.parentElement;
        depth++;
      }
      return parts.join(' > ').slice(0, 500);
    } catch (e) { return null; }
  }

  var INTERACTIVE_TAGS = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL', 'SUMMARY'];
  function isInteractive(el) {
    try {
      var node = el, depth = 0;
      while (node && node.nodeType === 1 && depth < 5) {
        if (INTERACTIVE_TAGS.indexOf(node.tagName) !== -1) return true;
        var role = node.getAttribute && node.getAttribute('role');
        if (role && ['button', 'link', 'tab', 'checkbox', 'radio', 'menuitem', 'option'].indexOf(role) !== -1) return true;
        if (node.onclick || (node.hasAttribute && node.hasAttribute('tabindex'))) return true;
        node = node.parentElement;
        depth++;
      }
      if (el && el.nodeType === 1 && window.getComputedStyle(el).cursor === 'pointer') return true;
    } catch (e) { /* noop */ }
    return false;
  }

  function isCheckoutClick(el) {
    try {
      return !!(el.closest && el.closest(
        'a[href*="/checkout"], button[name="checkout"], ' +
        '.shopify-payment-button__button, form[action*="/checkout"] [type="submit"]'
      ));
    } catch (e) { return false; }
  }

  // Rage-click detection
  var recentClicks = [];
  function detectRage(x, y, now) {
    recentClicks.push({ x: x, y: y, t: now });
    recentClicks = recentClicks.filter(function (c) { return now - c.t < RAGE_WINDOW; });
    var near = recentClicks.filter(function (c) {
      var dx = c.x - x, dy = c.y - y;
      return Math.sqrt(dx * dx + dy * dy) < RAGE_RADIUS;
    });
    return near.length >= RAGE_THRESHOLD;
  }

  document.addEventListener('click', function (e) {
    try {
      var now = Date.now();
      totalClicks++;
      var target = e.target;
      var isDead = !isInteractive(target);
      var rage = detectRage(e.clientX, e.clientY, now);
      var doc = document.documentElement;
      var docW = Math.max(doc.scrollWidth, window.innerWidth);
      var docH = Math.max(doc.scrollHeight, window.innerHeight);
      var pageX = e.clientX + window.scrollX;
      var pageY = e.clientY + window.scrollY;
      if (rage) {
        rageClicks++;
        pushEvent({ type: 'rage_click', x: e.clientX, y: e.clientY, target: cssSelector(target) });
      }
      if (isDead) {
        pushEvent({ type: 'dead_click', x: e.clientX, y: e.clientY, target: cssSelector(target) });
      }
      if (isCheckoutClick(target)) {
        completed = true;
        pushEvent({ type: 'checkout_start', x: e.clientX, y: e.clientY, target: cssSelector(target) });
      }
      pushEvent({
        type: 'click',
        x: e.clientX,
        y: e.clientY,
        target: cssSelector(target),
        data: {
          // Page-based percents (scroll-corrected, vs the full document) —
          // the meaningful coordinates for storefront click maps
          xPercent: pageX / docW,
          yPercent: pageY / docH,
          // Viewport-relative percents kept for reference
          vxPercent: e.clientX / window.innerWidth,
          vyPercent: e.clientY / window.innerHeight,
          pageX: pageX,
          pageY: pageY,
          docW: docW,
          docH: docH,
          viewportW: window.innerWidth,
          viewportH: window.innerHeight,
          device: device,
          pageType: PAGE_TYPE,
          elementText: (target && target.textContent || '').slice(0, 60).trim() || null,
          isDead: isDead
        }
      });
      // error_click: JS error within 1s of a click
      var onErr = function () {
        pushEvent({ type: 'error_click', x: e.clientX, y: e.clientY, target: cssSelector(target) });
        window.removeEventListener('error', onErr);
      };
      window.addEventListener('error', onErr);
      setTimeout(function () { window.removeEventListener('error', onErr); }, 1000);
    } catch (err) { /* never break the store */ }
  }, { capture: true, passive: true });

  // Scroll depth (emit at >=5pt changes, throttled)
  var lastScrollEmit = 0, lastScrollPct = -10;
  function scrollPercent() {
    var doc = document.documentElement;
    var scrollable = doc.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return 100;
    return Math.min(100, Math.round((window.scrollY / scrollable) * 100));
  }
  window.addEventListener('scroll', function () {
    try {
      var pct = scrollPercent();
      if (pct > maxScroll) maxScroll = pct;
      var now = Date.now();
      if (now - lastScrollEmit > 500 && Math.abs(pct - lastScrollPct) >= 5) {
        lastScrollEmit = now;
        lastScrollPct = pct;
        pushEvent({ type: 'scroll', scrollY: Math.round(window.scrollY), scrollPct: pct });
      }
    } catch (err) { /* noop */ }
  }, { passive: true });

  // Thrashed-cursor detection (mousemove sampled locally; no mousemove rows)
  var lastMoves = [];
  var lastMoveSample = 0;
  window.addEventListener('mousemove', function (e) {
    try {
      var now = Date.now();
      if (now - lastMoveSample < 100) return;
      lastMoveSample = now;
      lastMoves.push({ x: e.clientX, y: e.clientY, t: now });
      lastMoves = lastMoves.filter(function (m) { return now - m.t < THRASH_WINDOW; });
      if (lastMoves.length >= THRASH_CHANGES + 1) {
        var changes = 0;
        for (var i = 2; i < lastMoves.length; i++) {
          var v1x = lastMoves[i - 1].x - lastMoves[i - 2].x;
          var v2x = lastMoves[i].x - lastMoves[i - 1].x;
          var v1y = lastMoves[i - 1].y - lastMoves[i - 2].y;
          var v2y = lastMoves[i].y - lastMoves[i - 1].y;
          if (v1x * v2x + v1y * v2y < 0) changes++;
        }
        if (changes >= THRASH_CHANGES) {
          pushEvent({ type: 'thrashed_cursor', x: e.clientX, y: e.clientY });
          lastMoves = [];
        }
      }
    } catch (err) { /* noop */ }
  }, { passive: true });

  window.addEventListener('resize', function () {
    pushEvent({ type: 'resize', data: { width: window.innerWidth, height: window.innerHeight } });
  });

  function handleResponse(res) {
    if (res.status === 503) { dead = true; return null; }
    if (res.status === 429) {
      // back off for 2 minutes instead of hammering a saturated limiter
      backoffUntil = Date.now() + 120000;
      throw new Error('rate_limited');
    }
    if (!res.ok) throw new Error('flush failed');
    return res;
  }

  function flushHeatmap(isUnload) {
    if (dead || Date.now() < backoffUntil) return;
    if (heatmapInFlight && !isUnload) return;
    try {
      // Every payload is a full idempotent upsert (token + meta + stats),
      // so beacons and retries can never duplicate the session row.
      if (!isUnload && events.length === 0 && tick % 6 !== 0 && tick !== 1) return;
      var drained = events.splice(0);
      var payload = {
        pageViewToken: PAGE_VIEW_TOKEN,
        meta: sessionMeta,
        events: drained,
        stats: {
          duration: Date.now() - startTime,
          maxScroll: maxScroll,
          totalClicks: totalClicks,
          rageClicks: rageClicks,
          completed: completed,
          maxStep: 0,
          totalQuestions: 0
        }
      };
      var body = JSON.stringify(payload);
      if (isUnload && navigator.sendBeacon) {
        // Heatmap payloads are small; on beacon rejection fall back to
        // keepalive fetch, then requeue.
        if (!navigator.sendBeacon(HEATMAP_ENDPOINT, body)) {
          fetch(HEATMAP_ENDPOINT, { method: 'POST', body: body, keepalive: true })
            .catch(function () { /* page is going away */ });
        }
        return;
      }
      heatmapInFlight = true;
      fetch(HEATMAP_ENDPOINT, { method: 'POST', body: body, keepalive: !!isUnload })
        .then(handleResponse)
        .catch(function () {
          if (drained.length) {
            events = drained.concat(events).slice(0, MAX_EVENT_BUFFER);
          }
        })
        .then(function () { heatmapInFlight = false; }, function () { heatmapInFlight = false; });
    } catch (err) { /* never break the store */ }
  }

  setInterval(function () { tick++; flushHeatmap(false); }, FLUSH_MS);

  // ════════════════════════════════════════════════════════════════════════
  // Pipeline 2 — rrweb session replay (sampled)
  // ════════════════════════════════════════════════════════════════════════
  var replayBuf = [];
  var replayTimer = null;
  var stopRecording = null;
  var replayFails = 0;
  var REPLAY_SID = null;

  var shouldRecord = false;
  try {
    var flag = sessionStorage.getItem('pw_replay_on');
    if (flag === null) {
      flag = Math.random() < RECORDING_SAMPLE_RATE ? '1' : '0';
      sessionStorage.setItem('pw_replay_on', flag);
    }
    shouldRecord = flag === '1';
  } catch (e) { shouldRecord = false; }

  function replayMeta() {
    return {
      device: device,
      viewportW: window.innerWidth,
      viewportH: window.innerHeight,
      userAgent: navigator.userAgent,
      referrer: document.referrer || null,
      quizSlug: PAGE_TYPE,
      visitorSid: VISITOR_SID   // joins the recording to its heatmap sessions
    };
  }

  // Unload path: sendBeacon rejects bodies over its ~64KB quota, and an
  // rrweb full snapshot is routinely bigger. Chunk the batch so each beacon
  // fits; check every return value; fall back to keepalive fetch; requeue
  // what could not be handed off.
  function beaconReplayChunked(batch) {
    var chunk = [];
    var chunkChars = 0;
    var pending = batch.slice();
    var requeue = [];
    while (pending.length || chunk.length) {
      var next = pending.length ? pending[0] : null;
      var nextChars = next ? JSON.stringify(next).length : 0;
      var flushNow = !next || (chunk.length && chunkChars + nextChars > BEACON_CHUNK_BYTES);
      if (flushNow) {
        var body = JSON.stringify({ sessionId: REPLAY_SID, events: chunk, meta: replayMeta() });
        var sent = false;
        try { sent = navigator.sendBeacon(REPLAY_ENDPOINT, body); } catch (e) { sent = false; }
        if (!sent) {
          if (body.length < BEACON_CHUNK_BYTES) {
            fetch(REPLAY_ENDPOINT, { method: 'POST', body: body, keepalive: true })
              .catch(function () { /* page going away */ });
          } else {
            requeue = requeue.concat(chunk);
          }
        }
        chunk = [];
        chunkChars = 0;
        if (!next) break;
      } else {
        chunk.push(pending.shift());
        chunkChars += nextChars;
      }
    }
    if (requeue.length) {
      replayBuf = requeue.concat(replayBuf).slice(0, MAX_REPLAY_BUFFER);
    }
  }

  function flushReplay(isUnload) {
    if (dead || !REPLAY_SID || replayBuf.length === 0) return;
    if (!isUnload && Date.now() < backoffUntil) return;
    var batch = replayBuf.splice(0);
    if (isUnload && navigator.sendBeacon) {
      beaconReplayChunked(batch);
      return;
    }
    var body = JSON.stringify({ sessionId: REPLAY_SID, events: batch, meta: replayMeta() });
    fetch(REPLAY_ENDPOINT, { method: 'POST', body: body, keepalive: !!isUnload })
      .then(handleResponse)
      .then(function (res) {
        if (res === null) { shutdownReplay(); return; }
        replayFails = 0;
      })
      .catch(function () {
        replayBuf = batch.concat(replayBuf).slice(0, MAX_REPLAY_BUFFER);
        replayFails++;
        if (replayFails >= 5) shutdownReplay(); // stop wasting CPU/battery
      });
  }

  function shutdownReplay() {
    try {
      if (stopRecording) stopRecording();
      if (replayTimer) clearInterval(replayTimer);
      replayBuf = [];
    } catch (e) { /* noop */ }
  }

  function startRecording() {
    try {
      if (!window.rrweb || typeof window.rrweb.record !== 'function') return;
      REPLAY_SID = makeSid('pw_replay_sid', 'rs_');
      stopRecording = window.rrweb.record({
        emit: function (ev) {
          replayBuf.push(ev);
          if (replayBuf.length > MAX_REPLAY_BUFFER) replayBuf.splice(0, replayBuf.length - MAX_REPLAY_BUFFER);
        },
        maskAllInputs: true,          // NEVER record typed PII on the store
        sampling: {
          mousemove: true,
          mouseInteraction: true,
          scroll: 150,
          media: 800,
          input: 'last'
        },
        inlineStylesheet: true,
        inlineImages: false,
        collectFonts: false,
        recordCanvas: false,
        checkoutEveryNms: 60000       // full snapshot every 60s
      });
      // Ship the full snapshot ASAP via normal fetch — waiting for the first
      // 10s interval would leave short page views' snapshots to the lossy
      // unload path.
      setTimeout(function () { flushReplay(false); }, 1500);
      replayTimer = setInterval(function () { flushReplay(false); }, REPLAY_FLUSH_MS);
    } catch (e) { /* never break the store */ }
  }

  if (shouldRecord) {
    // Defer rrweb load so it never competes with page render
    setTimeout(function () {
      try {
        var s = document.createElement('script');
        s.src = RRWEB_SRC;
        s.async = true;
        s.onload = startRecording;
        document.head.appendChild(s);
      } catch (e) { /* noop */ }
    }, 2000);
  }

  // ── Final flushes (single-shot; re-arms if the tab becomes visible again) ─
  var finalFlushed = false;
  function finalFlush() {
    if (finalFlushed) return;
    finalFlushed = true;
    flushHeatmap(true);
    flushReplay(true);
  }
  window.addEventListener('pagehide', finalFlush);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') finalFlush();
    else finalFlushed = false;
  });
})();
